import type { AdminDashboardStatsDto } from '@familychef/shared';
import { useQuery } from '@tanstack/react-query';
import { ChefHat, FlaskConical, Scale, Users, UsersRound } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { StatCard } from '@/components/common/StatCard';
import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { fetchAdminDashboardStats } from '@/lib/admin-api';

export function DashboardPage() {
  const { data: stats, isLoading } = useQuery<AdminDashboardStatsDto>({
    queryKey: ['dashboard-stats'],
    queryFn: fetchAdminDashboardStats,
  });

  return (
    <AppShell>
      <TopBar title="Dashboard" subtitle="Vue d'ensemble de FamilyChef" />

      <div className="p-4 md:p-6">
        {/* Stat Cards */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard
            label="Recettes"
            value={isLoading ? '—' : (stats?.recipes ?? 0)}
            icon={<ChefHat size={22} />}
            color="green"
          />
          <StatCard
            label="Ingrédients"
            value={isLoading ? '—' : (stats?.ingredients ?? 0)}
            icon={<Scale size={22} />}
            color="orange"
          />
          <StatCard
            label="Techniques"
            value={isLoading ? '—' : (stats?.techniques ?? 0)}
            icon={<FlaskConical size={22} />}
            color="blue"
          />
          <StatCard
            label="Utilisateurs"
            value={isLoading ? '—' : (stats?.users ?? 0)}
            icon={<Users size={22} />}
            color="purple"
          />
          <StatCard
            label="Familles"
            value={isLoading ? '—' : (stats?.families ?? 0)}
            icon={<UsersRound size={22} />}
            color="green"
          />
        </div>

        {/* Chart */}
        <div className="card mb-8">
          <h3 className="mb-4 font-semibold text-slate-900">Recettes créées par semaine</h3>
          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.weeklyActivity ?? []} barSize={28}>
                <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} name="Recettes" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Alerts */}
        <div className="card">
          <h3 className="mb-4 font-semibold text-slate-900">Alertes</h3>
          <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
            ✅ Tous les systèmes fonctionnent normalement
          </div>
        </div>
      </div>
    </AppShell>
  );
}
